import OMCalendarComponent, { isAllTimeRange } from "@/app/components/ui/Calendar/OMCalendar";
import NoItemFoundComponent from "@/app/components/ui/NoItemFoundComponent";
import SearchableMultiSelectMenu from "@/app/components/ui/SelectMenus/MultiSelectMenu";
import { IInvoiceProps, useCartInvoiceListAPI } from "@/app/hooks/api_hooks/Cart/useCartInvoiceListAPI";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useLoader } from "@/app/hooks/useLoader";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { useUtils } from "@/app/hooks/useUtils";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { IOrderManagementState } from "@/app_redux/reducers/slices/auth/order_management_slice";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import InvoiceCancellationModal from "../InvoiceCancellationModal";
import UrgentCancelItemsModal from "../UrgentCancelItemsModal";
import RemoveMembershipModal from "../RemoveMembershipModal";
import InvoiceReturnModalComponent from "../InvoiceReturnModalComponent";
import InvoiceRefundModalComponent from "../InvoiceRefundModalComponent";
import { ICartState, setInvoiceListFilter } from "@/app_redux/reducers/slices/auth/cart_slice";
import { useDispatch } from "react-redux";
import { useUserInvoiceStatusList } from "@/app/hooks/api_hooks/Cart/useUserInvoiceStatusList";
import { usePaymentUserWalletAPI } from "@/app/hooks/api_hooks/payment/usePaymentUserWalletAPI";
import { ICartFilterBody, ICartUserInvoiceListResponseListData } from "@/api_framework/api_modals/Cart";
import { useCartListAPI } from "@/app/hooks/api_hooks/Cart/useCartListAPI";
import RBACFlag from "@/app/components/core/RBACFlag";
import CartHeaderComponent from "../../CartBillingComponent/CartHeaderComponent";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";
import { Menu } from "@headlessui/react";
import { BsThreeDots } from "react-icons/bs";
import InvoiceReturnClinicModalComponent from "../InvoiceReturnClinicModal";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import MappedClinicAndCityPage from "@/app/routes/error_routes/MapClinicAndCityPage";
import OverDueModalComponent from "@components/routes/auth_routes/ClinicClosingComponent/OverDueModalComponent";
import TableSkeletonComponent from "@/app/components/ui/LoaderComponent";
import InvoiceCancelWindowGate from "../../InvoiceCancelWindowGate";
import {
  isUrgentCancelAllowedUser,
  isWithinUrgentCancelWindow,
  isWithinVoucherPaidCancelWindow,
  isUrgentCancelEligibleStatus,
  getInvoiceCancelWindowSeconds,
} from "@/constants";

export interface InvoiceFilter {
  status: Array<string>;
}

const tableHeader = [
  { item: "Invoice ID" },
  { item: "Status" },
  { item: "Invoice Total" },
  { item: "Paid Amount" },
  { item: "Return Amount" },
  { item: "Created Date & Time" },
  { item: "Clinic" },
  { item: "Created by" },
  { item: "Order Summary" },
  { item: "Action" },
];

const InvoiceListingComponent = () => {
  const { NO_CART } = useImageConstants();
  const { veticDateFormat, InvoicestatusBackgroundColor, InvoiceStatusDataTransformed } = useUtils();
  const { execute: fetchInvoiceList } = useCartInvoiceListAPI();
  const isCartInvoiceLoading = useLoader("isCustomerInvoiceTableInProgress");
  const { getRouteKey } = useRouter();
  const routeId = getRouteKey("ORDER_MANAGEMENT", "id");
  const { routeState } = useRouteInfo(routeId);
  const [initialFetch, setInitialFetch] = useState(false);
  const { inoviceList } = useRouteInfo(getRouteKey("CART_MANAGEMENT", "id")).routeState.state as ICartState;
  const { customerId } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id")).routeState.state as ICustomerProfileState;
  const { orderclinicList } = routeState.state as IOrderManagementState;
  const { execute: fetchInvoiceStatus, invoiceStatus } = useUserInvoiceStatusList();
  const { execute: fetchUserWalletDetails } = usePaymentUserWalletAPI();
  const { execute: fetchCartList } = useCartListAPI();
  const dispatch = useDispatch();

  const getDefaultDateRange = () => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);
    return { startDate, endDate };
  };

  const [selectionDate, setSelectionDate] = useState({
    startDate: getDefaultDateRange().startDate as Date | null,
    endDate: getDefaultDateRange().endDate as Date | null,
    key: "selectionDate",
  });

  const formatDate = (date: Date | null) => {
    if (!date || isAllTimeRange(date)) return null;
    const d = new Date(date);
    return `${d.getDate().toString().padStart(2, "0")}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getFullYear()}`;
  };
  const isAllRangeSelected =
    isAllTimeRange(selectionDate.startDate) && isAllTimeRange(selectionDate.endDate);
  const formattedStartDate = formatDate(selectionDate.startDate);
  const formattedEndDate = formatDate(selectionDate.endDate);
  const [invoiceId, setInvoiceId] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState(null);
  // Clinic of the invoice being urgent-cancelled, kept apart from the return /
  // cancel clinic prompt: urgent cancel never asks the user to switch clinic.
  const [urgentCancelClinic, setUrgentCancelClinic] = useState<any>(null);
  const [returnState, setReturnState] = useState(false);
  const [cancelState, setCancelState] = useState(false);
  const [isUrgentCancel, setIsUrgentCancel] = useState(false); // Urgent-cancel mode for the cancel modal
  const [urgentCancelItemsState, setUrgentCancelItemsState] = useState(false); // Item-list modal for urgent cancel
  // Invoice whose Snapmint remove-membership confirmation is open
  const [removeMembershipInvoice, setRemoveMembershipInvoice] = useState<ICartUserInvoiceListResponseListData | null>(null);
  const [_pendingCancel, setPendingCancel] = useState(false);
  const [refundState, setRefundState] = useState(false);
  const [invoiceType, setInvoiceType] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [returnClinicState, setReturnClinicState] = useState(false);
  const [cancelClinicState, setCancelClinicState] = useState(false);
  const [isVisibleMappedClinic, setIsVisibleMappedClinic] = useState<boolean>(false);
  const { mappedClinicId } = useAppSelector((state) => state.root.UIStates);
  const userEmail = useAppSelector((state) => state.auth?.user?.email);
  const invoiceStatusItem = useSelectMenuReducer(invoiceStatus, "name", "id");

  const [examption, setExamption] = useState(false);
  const [paymentDetails, setPpaymentDetails] = useState(null);

  const formHook = useForm<InvoiceFilter>({
    mode: "onChange",
    defaultValues: {},
  });

  useEffect(() => {
    if (!initialFetch && orderclinicList?.length > 0 && formHook.watch("status")?.length > 0 && customerId) {
      const constructedBody: IInvoiceProps = {
        user_id: customerId,
        start_date: formattedStartDate ?? null,
        end_date: formattedEndDate ?? null,
        statuses: formHook.watch("status"),
        logged_in_clinic_id: mappedClinicId,
      };
      fetchInvoiceList(constructedBody, "invoice_listing_page");
      dispatch(setInvoiceListFilter(constructedBody));
      setInitialFetch(true);
    }
  }, [orderclinicList?.length > 0, formHook.watch("status"), customerId]);

  useEffect(() => {
    fetchInvoiceStatus();
  }, []);

  const fetchInvoiceListAPI = () => {
    const constructedBody: IInvoiceProps = {
      user_id: customerId,
      start_date: isAllRangeSelected ? "1" : (formattedStartDate ?? null),
      end_date: isAllRangeSelected ? "1" : (formattedEndDate ?? null),
      statuses: formHook.watch("status"),
      logged_in_clinic_id: mappedClinicId,
    };
    fetchInvoiceList(constructedBody, "invoice_listing_page");
  };

  useEffect(() => {
    if (customerId) {
      fetchUserWalletDetails(customerId);
      const filterBody: ICartFilterBody = {
        cart_status: ["Unbilled"],
        start_date: null,
        end_date: null,
      };
      fetchCartList(filterBody, customerId);
    }
  }, [customerId]);

  const onSubmit = (data: InvoiceFilter) => {
    const constructedBody: IInvoiceProps = {
      user_id: customerId,
      start_date: isAllRangeSelected ? "1" : (formattedStartDate ?? null),
      end_date: isAllRangeSelected ? "1" : (formattedEndDate ?? null),
      logged_in_clinic_id: mappedClinicId,
      statuses: data.status,
    };
    fetchInvoiceList(constructedBody, "invoice_listing_page");
    dispatch(setInvoiceListFilter(constructedBody));
  };

  const handleRowSelect = (invoice: any) => {
    if (["partially_paid", "unpaid"].includes(invoice?.status?.toLowerCase())) {
      window.open(`/app/user-cart/cart-billing/${customerId}?from_cart=true`, "_blank", "noopener,noreferrer");
    }
  };

  // RBAC: CART_CENTRAL_INVOICE_ACTIONS — legacy role: central_ops
  const isCentralOpps = useFeatureAccess("CART_CENTRAL_INVOICE_ACTIONS");

  // Urgent cancel: gated by a hardcoded email allowlist, not RBAC.
  const isUrgentCancelUser = isUrgentCancelAllowedUser(userEmail);

  return (
    <div>
      <div className="bg-white px-2 py-0">
        <div className="flex justify-between items-center">
          <p className="text-sm  leading-6 font-semibold ">Invoices</p>
        </div>
        <CartHeaderComponent />
      </div>
      <div className="bg-white pl-1 my-0">
        <form onSubmit={formHook.handleSubmit(onSubmit)} className="p-1">
          <div className="items-center grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">

            <OMCalendarComponent
              selectionDate={selectionDate}
              setSelectionDate={setSelectionDate}
              startDate={isAllRangeSelected ? "Select" : formattedStartDate}
              endDate={isAllRangeSelected ? "all" : formattedEndDate}
              handelClick={() => undefined}
              isPaddingOnTop={false}
              isShowSelectAll={true}
            />
            <SearchableMultiSelectMenu
              onSelectItem={(items) => {
                if (items) {
                  const status = items.map((i) => i.id);
                  formHook.setValue("status", status);
                }
              }}
              withSelectAll
              defaultSelectAll={true}
              showTooltips
              withDropDownSearch
              errorMessages={[{ message: "Required", type: "required" }]}
              fieldError={formHook.formState.errors.status}
              register={formHook.register("status", {
                required: true,
              })}
              showTypedErrors={false}
              placeholder="Select Status"
              defaultSelected={invoiceStatusItem?.filter((c) => formHook.watch("status")?.includes(c.id))}
              selectItems={invoiceStatusItem}
            />
            <button className="bg-black py-2 text-xs text-white font-medium rounded-md px-3 w-32" type="submit">
              Search
            </button>
          </div>
        </form>
      </div>

      {isCartInvoiceLoading ? (
        <TableSkeletonComponent columnLength={10} bodyLength={8} />
      ) : inoviceList?.length > 0 ? (
        <div className="px-0 sm:px-2 lg:px-2 mt-3">
          <div className="mt-1 flow-root">
            <div
              className="outer-container relative h-[450px] 2xl:h-[550px] rounded-xl bg-[#F9FAFB] "
              style={{
                boxShadow: "0px 4px 4px 0px rgba(61, 70, 112, 0.08)",
                borderRadius: "12px",
              }}
            >
              <div className="scroll-inner">
                <div className="bg-white rounded-lg">
                  <table className="w-full border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                    <thead className="bg-[#F5F5F5] text-[#1F1F23] top-0 font-normal">
                      <tr>
                        {tableHeader.map((key, index) => (
                          <th
                            key={index}
                            scope="col"
                            className={`py-3 px-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] capitalize ${key.item === "Action"
                              ? "table-cell sm:sticky right-0 z-20 bg-[#F5F5F5] min-w-[100px] w-[100px] max-w-[100px] text-center"
                              : ""
                              }`}
                          >
                            {key.item}
                          </th>
                        ))}
                      </tr>
                    </thead>

                    <tbody className="overflow-hidden rounded-xl w-full">
                      {inoviceList?.map((cl, index) => {
                        const isFirst = index === 0;
                        const isLast = index === inoviceList.length - 1;
                        const isSingle = inoviceList.length === 1;

                        return (
                          <tr
                            key={index}
                            className={`group bg-white transition ${isSingle ? "rounded-xl" : ""} ${isFirst && !isSingle ? "rounded-t-xl" : ""
                              } ${isLast && !isSingle ? "rounded-b-xl" : ""} ${cl?.status?.toLowerCase() === "partially_paid" ? "cursor-pointer" : ""
                              }`}
                          >
                            {/* Invoice ID */}
                            <td
                              className={`px-3 py-3 whitespace-nowrap text-xs font-normal ${isSingle ? "rounded-tl-2xl rounded-bl-2xl" : ""
                                } ${isFirst && !isSingle ? "rounded-tl-2xl" : ""} ${isLast && !isSingle ? "rounded-bl-2xl" : ""
                                }`}
                            >
                              <p
                                className="text-xs cursor-pointer"
                                onClick={() => handleRowSelect(cl)}
                              >
                                {cl?.invoice_number}
                              </p>
                              <div className="flex gap-1 items-center">
                                <RBACFlag actionId="VIEW_INVOICE_ACTION_CTA">
                                  <a
                                    className="text-[10px] text-[#0B6EFF] font-medium cursor-pointer p-0 m-0"
                                    href={cl?.pdf_url}
                                    target="_blank"
                                  >
                                    View PDF
                                  </a>
                                </RBACFlag>
                                {[
                                  "partially_paid",
                                  "paid",
                                  "partially_refunded",
                                  "partially_returned",
                                  "refund_requested",
                                  "refund rejected",
                                ].includes(cl?.status?.toLowerCase()) &&
                                  cl?.is_return_eligible && (
                                    <RBACFlag actionId="RETURN_INVOICE_ACTION_CTA">
                                      <button
                                        className="text-[10px] text-[#0B6EFF] font-medium relative cursor-pointer p-0 m-0"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setInvoiceId(cl?.invoice_id);
                                          setReturnState(false);
                                          setInvoiceType("Return");
                                          setSelectedInvoice(cl?.clinic_details);
                                          setReturnClinicState(true);
                                          if (cl?.clinic_details?.id === mappedClinicId || cl?.is_same_clinic) {
                                            setReturnState(true);
                                          } else {
                                            setReturnClinicState(true);
                                          }
                                        }}
                                      >
                                        <span className="pl-1">Return</span>
                                        <span className="absolute top-1/2 left-0 transform -translate-x-1/2 -translate-y-1/2 h-[10px] border-l border-[#8B909A]"></span>
                                      </button>
                                    </RBACFlag>
                                  )}
                                {[
                                  "partially_paid",
                                  "paid",
                                  "partially_refunded",
                                  "partially_returned",
                                  "refund_rejected",
                                ].includes(cl?.status?.toLowerCase()) &&
                                  cl?.is_refund_eligible && (
                                    <RBACFlag actionId="REFUND_INVOICE_ACTION_CTA">
                                      <button
                                        className="text-[10px] text-[#0B6EFF] font-medium relative cursor-pointer p-0 m-0"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setInvoiceId(cl?.invoice_id);
                                          setRefundState(true);
                                          setInvoiceType("Refund");
                                        }}
                                      >
                                        <span className="pl-1">Refund</span>
                                        <span className="absolute top-1/2 left-0 transform -translate-x-1/2 -translate-y-1/2 h-[10px] border-l border-[#8B909A]"></span>
                                      </button>
                                    </RBACFlag>
                                  )}
                                {isUrgentCancelUser &&
                                  isUrgentCancelEligibleStatus(cl?.status) &&
                                  isWithinUrgentCancelWindow(cl?.created_at) && (
                                    <button
                                      className="text-[10px] text-[#F72F03] font-medium relative cursor-pointer p-0 m-0"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setInvoiceId(cl?.invoice_id);
                                        setInvoiceNumber(cl?.invoice_number);
                                        // The session runs against the clinic that
                                        // raised the invoice, whichever clinic the
                                        // user is logged into.
                                        setUrgentCancelClinic(cl?.clinic_details);
                                        setIsUrgentCancel(true);
                                        setCancelState(false);
                                        setUrgentCancelItemsState(true);
                                      }}
                                    >
                                      <span className="pl-1">Urgent Cancel</span>
                                      <span className="absolute top-1/2 left-0 transform -translate-x-1/2 -translate-y-1/2 h-[10px] border-l border-[#8B909A]"></span>
                                    </button>
                                  )}
                                {["voucher_paid"].includes(cl?.status?.toLowerCase()) ? (
                                  isWithinVoucherPaidCancelWindow(cl?.created_at) && (
                                  <button
                                    className="text-[10px] text-[#0B6EFF] font-medium relative cursor-pointer p-0 m-0"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setIsUrgentCancel(false);
                                      setInvoiceId(cl?.invoice_id);
                                      setCancelState(false);
                                      setSelectedInvoice(cl?.clinic_details);
                                      setPendingCancel(true);
                                      if (cl?.clinic_details?.id === mappedClinicId || cl?.is_same_clinic) {
                                        setCancelState(true);
                                      } else {
                                        setCancelClinicState(true);
                                      }
                                    }}
                                  >
                                    <span className="pl-1">Cancel</span>
                                    <span className="absolute top-1/2 left-0 transform -translate-x-1/2 -translate-y-1/2 h-[10px] border-l border-[#8B909A]"></span>
                                  </button>
                                  )
                                ) : (
                                  ["unpaid"].includes(cl?.status?.toLowerCase()) && (
                                    <RBACFlag actionId="CANCEL_INVOICE_ACTION_CTA">
                                      {(!isUrgentCancelUser ||
                                          isWithinUrgentCancelWindow(cl?.created_at)) && (
                                        <InvoiceCancelWindowGate
                                          createdAt={cl?.created_at}
                                          windowSeconds={getInvoiceCancelWindowSeconds({
                                            isUrgentCancelUser,
                                            isCentralOpps,
                                            isSnapmintEligible: !!cl?.snapmint_meta_data?.is_eligible,
                                          })}
                                        >
                                        <button
                                          className="text-[10px] text-blue-400 font-medium relative cursor-pointer p-0 m-0"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setIsUrgentCancel(false);
                                            setInvoiceId(cl?.invoice_id);
                                            setCancelState(false);
                                            setSelectedInvoice(cl?.clinic_details);
                                            setPendingCancel(true);
                                            if (cl?.clinic_details?.id === mappedClinicId || cl?.is_same_clinic) {
                                              setCancelState(true);
                                            } else {
                                              setCancelClinicState(true);
                                            }
                                          }}
                                        >
                                          <span className="pl-1">Cancel</span>
                                          <span className="absolute top-1/2 left-0 transform -translate-x-1/2 -translate-y-1/2 h-[10px] border-l border-[#8B909A]"></span>
                                        </button>
                                        </InvoiceCancelWindowGate>
                                      )}
                                    </RBACFlag>
                                  )
                                )}
                                {cl?.snapmint_inclusive_remove_membership && (
                                  <RBACFlag actionId="REMOVE_MEMBERSHIP_ACTION_CTA">
                                    <button
                                      type="button"
                                      title="Snapmint EMI not completed: remove the memberships from this invoice"
                                      className="text-[10px] text-[#F72F03] font-medium relative cursor-pointer p-0 m-0"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setRemoveMembershipInvoice(cl);
                                      }}
                                    >
                                      <span className="pl-1">Remove Membership</span>
                                      <span className="absolute top-1/2 left-0 transform -translate-x-1/2 -translate-y-1/2 h-[10px] border-l border-[#8B909A]"></span>
                                    </button>
                                  </RBACFlag>
                                )}
                              </div>
                            </td>

                            {/* Status */}
                            <td
                              className="p-3 whitespace-nowrap text-xs font-normal"
                              onClick={() => handleRowSelect(cl)}
                            >
                              <button
                                className={`${InvoicestatusBackgroundColor(cl?.status?.toLowerCase())} text-xs py-2 min-w-[120px] px-3 rounded-full flex items-center justify-center text-black`}
                              >
                                {InvoiceStatusDataTransformed(cl?.status)}
                              </button>
                            </td>

                            {/* Invoice Total */}
                            <td
                              className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#6C727F]"
                              onClick={() => handleRowSelect(cl)}
                            >
                              {cl?.invoice_total}
                            </td>

                            {/* Paid Amount */}
                            <td
                              className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#6C727F]"
                              onClick={() => handleRowSelect(cl)}
                            >
                              {cl?.paid_amount}
                            </td>

                            {/* Return Amount */}
                            <td
                              className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#6C727F]"
                              onClick={() => handleRowSelect(cl)}
                            >
                              {cl?.return_amount}
                            </td>

                            {/* Created Date & Time */}
                            <td
                              className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#6C727F]"
                              onClick={() => handleRowSelect(cl)}
                            >
                              {cl?.created_at ? veticDateFormat(cl?.created_at) : "NA"}
                            </td>

                            {/* Clinic */}
                            <td
                              className="px-3 py-3 whitespace-nowrap text-xs font-normal"
                              onClick={() => handleRowSelect(cl)}
                            >
                              <div className="flex flex-col overflow-hidden">
                                <span
                                  className="block w-full truncate"
                                  title={cl?.clinic_details?.name || "NA"}
                                >
                                  {cl?.clinic_details?.name || "NA"}
                                </span>
                              </div>
                            </td>

                            {/* Created By */}
                            <td
                              className="px-3 py-3 whitespace-nowrap text-xs font-normal"
                              onClick={() => handleRowSelect(cl)}
                            >
                              <span className="text-blue-600">{cl?.created_by || "NA"}</span>
                            </td>

                            {/* Order Summary */}
                            <td
                              className="px-3 py-3 whitespace-nowrap text-xs font-normal"
                              onClick={() => handleRowSelect(cl)}
                            >
                              {cl?.order_summary_url ? (
                                <a
                                  className="text-black font-medium underline cursor-pointer"
                                  href={cl?.order_summary_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  View Summary
                                </a>
                              ) : (
                                <span className="text-[#6C727F]">NA</span>
                              )}
                            </td>

                            {/* Action */}
                            <td
                              className={`table-cell px-3 py-3 whitespace-normal text-xs font-normal sm:sticky right-0 bg-gray-100 align-middle min-w-[100px] w-[100px] max-w-[100px] ${isSingle ? "rounded-tr-2xl rounded-br-2xl" : ""
                                } ${isFirst && !isSingle ? "rounded-tr-2xl" : ""} ${isLast && !isSingle ? "rounded-br-2xl" : ""
                                }`}
                            >
                              {["partially_paid", "unpaid"].includes(cl?.status?.toLowerCase()) && (
                                <RBACFlag actionId="CART_BILLING">
                                  <Menu as="div" className="relative h-8 w-8 mx-auto">
                                    <Menu.Button className="w-full h-full flex items-center justify-center rounded-md bg-[#f1f1f1] hover:bg-gray-200 transition-colors">
                                      <BsThreeDots className="w-5 h-5 text-gray-600" />
                                    </Menu.Button>
                                    <Menu.Items className="absolute right-12 top-1/2 -translate-y-1/2 w-48 origin-top-right bg-white shadow-xl border border-gray-100 rounded-lg z-[80] focus:outline-none">
                                      <div className="py-1">
                                        <Menu.Item>
                                          {({ active }) => (
                                            <a
                                              href="#"
                                              className={`block px-4 py-2 text-sm transition-colors ${active ? "bg-gray-50 text-black" : "text-gray-700"
                                                }`}
                                              onClick={() => handleRowSelect(cl)}
                                            >
                                              SETTLE NOW
                                            </a>
                                          )}
                                        </Menu.Item>
                                        <Menu.Item>
                                          {({ active }) => (
                                            <a
                                              href="#"
                                              className={`block px-4 py-2 text-sm transition-colors ${active ? "bg-gray-50 text-black" : "text-gray-700"
                                                }`}
                                              onClick={() => {
                                                setPpaymentDetails(cl);
                                                setExamption(true);
                                              }}
                                            >
                                              RAISE OVERDUE
                                            </a>
                                          )}
                                        </Menu.Item>
                                      </div>
                                    </Menu.Items>
                                  </Menu>
                                </RBACFlag>
                              )}
                            </td>
                          </tr>
                        );
                      })}

                      {/* ── Modals ── */}
                      {examption && (
                        <OverDueModalComponent
                          open={examption}
                          setOpen={setExamption}
                          paymentDetails={paymentDetails}
                          fetchInvoiceList={fetchInvoiceListAPI}
                        />
                      )}
                      {cancelState && (
                        <InvoiceCancellationModal
                          open={cancelState}
                          setOpen={setCancelState}
                          invoiceId={invoiceId}
                          source="invoice_listing_page"
                          isUrgentCancel={isUrgentCancel}
                        />
                      )}
                      {urgentCancelItemsState && (
                        <UrgentCancelItemsModal
                          open={urgentCancelItemsState}
                          setOpen={setUrgentCancelItemsState}
                          invoiceId={invoiceId}
                          invoiceNumber={invoiceNumber}
                          clinicId={urgentCancelClinic?.id}
                          clinicName={urgentCancelClinic?.name}
                          source="invoice_listing_page"
                        />
                      )}
                      {removeMembershipInvoice && (
                        <RemoveMembershipModal
                          open={!!removeMembershipInvoice}
                          setOpen={(value) => !value && setRemoveMembershipInvoice(null)}
                          invoice={removeMembershipInvoice}
                          onRemoved={() => {
                            fetchUserWalletDetails(customerId);
                            fetchInvoiceListAPI();
                          }}
                        />
                      )}
                      {cancelClinicState && (
                        <InvoiceReturnClinicModalComponent
                          open={cancelClinicState}
                          buttonSource="CANCEL"
                          setOpen={setCancelClinicState}
                          invoiceId={invoiceId}
                          source="invoice_listing_page"
                          clinic_details={selectedInvoice}
                          invoiceType={invoiceType}
                          onContinue={() => {
                            setCancelClinicState(false);
                            setCancelState(true);
                          }}
                          setIsVisibleMappedClinic={setIsVisibleMappedClinic}
                        />
                      )}
                      {isVisibleMappedClinic && (
                        <MappedClinicAndCityPage
                          isCrossVisible={true}
                          setIsVisibleMappedClinic={setIsVisibleMappedClinic}
                        />
                      )}
                      {returnClinicState && (
                        <InvoiceReturnClinicModalComponent
                          buttonSource="RETURN"
                          open={returnClinicState}
                          setOpen={setReturnClinicState}
                          invoiceId={invoiceId}
                          source="invoice_listing_page"
                          clinic_details={selectedInvoice}
                          invoiceType={invoiceType}
                          onContinue={() => {
                            setReturnClinicState(false);
                            setReturnState(true);
                          }}
                        />
                      )}
                      {returnState && (
                        <InvoiceReturnModalComponent
                          open={returnState}
                          setOpen={setReturnState}
                          invoiceId={invoiceId}
                          invoiceType={invoiceType}
                          source="invoice_listing_page"
                        />
                      )}
                      {refundState && (
                        <InvoiceRefundModalComponent
                          refundState={refundState}
                          setRefundState={setRefundState}
                          invoiceId={invoiceId}
                          invoiceType={invoiceType}
                          source="invoice_listing_page"
                        />
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        inoviceList?.length === 0 && (
          <div className="bg-white h-[600px] pt-2">
            <NoItemFoundComponent
              message=""
              description="No Invoice Found"
              imageURL={NO_CART}
              InputClassName="my-0 h-full"
            />
          </div>
        )
      )}
    </div>
  );
};

export default InvoiceListingComponent;